import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";

const PALETTE = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#A855F7", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#71717A"];

function ColorSwatches({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="color-swatches">
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${value?.toLowerCase() === c.toLowerCase() ? "ring-2 ring-offset-1 ring-zinc-900" : ""}`}
          style={{ background: c }}
          data-testid={`swatch-${c.replace("#", "")}`}
          aria-label={`Color ${c}`}
        />
      ))}
    </div>
  );
}

export default function PipelineManagerDialog({ open, onOpenChange, canEdit, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState(PALETTE[5]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pipeline-statuses");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const addStage = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      await api.post("/pipeline-statuses", { label, color: newColor });
      toast.success(`Stage "${label}" added`);
      setNewLabel("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add stage");
    }
  };

  const updateStage = async (id, patch) => {
    try {
      await api.put(`/pipeline-statuses/${id}`, patch);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update");
    }
  };

  const deleteStage = async (s) => {
    if (!window.confirm(`Delete "${s.label}"? Threads on this stage will be moved to Open.`)) return;
    try {
      await api.delete(`/pipeline-statuses/${s.id}`);
      toast.success(`Stage "${s.label}" deleted`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="pipeline-manager-dialog">
        <DialogHeader>
          <DialogTitle>Pipeline stages</DialogTitle>
          <DialogDescription>
            {canEdit
              ? "Add custom pipeline stages and pick a color for each. Default stages can be recolored but not deleted."
              : "View pipeline stages. Only admins can add or edit stages."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {loading && <p className="text-sm text-zinc-500">Loading…</p>}
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-2 border border-zinc-200 rounded-md px-2 py-1.5" data-testid={`pipeline-row-${s.id}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
              {canEdit ? (
                <Input
                  defaultValue={s.label}
                  className="h-7 text-sm flex-1"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== s.label) updateStage(s.id, { label: v });
                  }}
                  data-testid={`pipeline-label-${s.id}`}
                />
              ) : (
                <span className="flex-1 text-sm">{s.label}</span>
              )}
              {canEdit && (
                <details className="relative">
                  <summary className="cursor-pointer w-5 h-5 rounded-full border border-zinc-300 list-none [&::-webkit-details-marker]:hidden" style={{ background: s.color }} title="Change color" data-testid={`pipeline-color-${s.id}`} />
                  <div className="absolute right-0 mt-1 z-10 bg-white border border-zinc-200 rounded-md p-2 shadow-md">
                    <ColorSwatches value={s.color} onChange={(c) => updateStage(s.id, { color: c })} />
                  </div>
                </details>
              )}
              {s.is_system ? (
                <Lock className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} title="Built-in stage" />
              ) : canEdit ? (
                <button
                  onClick={() => deleteStage(s)}
                  className="p-1 text-zinc-400 hover:text-red-600 transition-colors"
                  data-testid={`pipeline-delete-${s.id}`}
                  title="Delete stage"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              ) : null}
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="border-t border-zinc-200 pt-3 mt-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">Add a stage</p>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: newColor }} />
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Proposal Sent"
                className="h-8 text-sm flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") addStage(); }}
                data-testid="pipeline-new-label"
              />
              <Button size="sm" className="h-8" onClick={addStage} data-testid="pipeline-add-button">
                <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2} /> Add
              </Button>
            </div>
            <div className="mt-2 pl-5">
              <ColorSwatches value={newColor} onChange={setNewColor} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pipeline-close-button">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
